import Highcharts from "highcharts";
import React from "react";
import t from "../../../../i18n";
import { color } from "@ui";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import testSerieData from "@ui/Highcharts/lib/testSerieData";
import Marker from "@ui/Highcharts/ui/Marker";
import { PointSymbol, PointSymbolStroke, SquareMarker } from "@ui/Highcharts/ui/PointSymbol";
import { MARKERS_VISIBLE_EVENT } from "@ui/Highcharts/plugins/improveDrawPoints";
import { FailureMetadata } from "../PointMetadata";
import validateInterface from "@ui/lib/validateInterface";
import { CLASSIC_FAILURE, MANUAL_FAILURE } from "../series/serieIds";

interface P {
  chartRef: React.RefObject<Highcharts.Chart>;
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesFlagsOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesScatterOptions
  )[];
  period: Period;
}

function DiagnosisLegend({ chartRef, series, period }: P) {
  const [markerEnabled, setMarkerEnabled] = React.useState(false);

  React.useLayoutEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      throw new Error("Missing chart");
    }
    const xAxis = chart.xAxis[0];
    if (!xAxis || xAxis.options.type !== "datetime") throw new Error("Wrong xAxis");
    Highcharts.addEvent(xAxis, MARKERS_VISIBLE_EVENT, ({ value }: { value: boolean }) => {
      setMarkerEnabled(value);
    });
  }, [chartRef]);

  const manualFailureSeries = series.filter(serie => serie.id?.includes(MANUAL_FAILURE));
  const classicFailureSeries = React.useMemo(
    () => series.filter(serie => serie.id?.includes(CLASSIC_FAILURE)),
    [series],
  );
  const hasManualFailure = React.useMemo(
    () => manualFailureSeries.some(serie => testSerieData(serie, period, p => p.y !== null)),
    [manualFailureSeries, period],
  );

  const faultStatus = React.useMemo(
    () =>
      classicFailureSeries
        .filter(serie => serie.visible)
        .flatMap(serie =>
          serie.data?.flatMap(data => {
            if (
              data &&
              typeof data === "object" &&
              "x" in data &&
              typeof data.x === "number" &&
              data.x >= period.from &&
              data.x <= period.to &&
              "custom" in data
            ) {
              const { failureStatus } = validateInterface(FailureMetadata, data.custom);
              return failureStatus;
            }
            return [];
          }),
        )
        .filter(Boolean),
    [classicFailureSeries, period],
  );

  const hasToBeAnalysedPoints = React.useMemo(
    () => faultStatus.some(status => status === "TO_BE_ANALYSED"),
    [faultStatus],
  );
  const hasFilledPoint = React.useMemo(
    () =>
      faultStatus.some(
        status => status === "CONFIRMED" || status === "REJECTED" || status === "LEGACY_REJECTED",
      ),
    [faultStatus],
  );
  const hasUnfulfilledPoint = React.useMemo(
    () => faultStatus.some(status => status === "SUSPECTED"),
    [faultStatus],
  );
  const hasClassicFailure = hasToBeAnalysedPoints || hasFilledPoint || hasUnfulfilledPoint;

  if (!markerEnabled) return null;

  return (
    <>
      {hasClassicFailure && (
        <>
          {hasToBeAnalysedPoints && (
            <Marker
              name={t("diagnosis.legend.marker.to_be_analysed")}
              description={t("commons.overview.header.legend.to_be_analysed.description")}
            >
              <PointSymbolStroke height={9} width={9} color={color.raw.titanium600} />
            </Marker>
          )}
          {hasFilledPoint && (
            <Marker
              name={t("commons.overview.header.legend.filled")}
              description={t("commons.overview.header.legend.filled.description")}
            >
              <PointSymbol
                height={9}
                width={9}
                color={color.raw.titanium600}
                fill={color.raw.titanium600}
              />
            </Marker>
          )}
          {hasUnfulfilledPoint && (
            <Marker
              name={t("commons.overview.header.legend.not_filled")}
              description={t("commons.overview.header.legend.not_filled.description")}
            >
              <PointSymbol height={9} width={9} color={color.raw.titanium600} />
            </Marker>
          )}
        </>
      )}
      {hasManualFailure && (
        <Marker
          name={t("commons.overview.header.legend.manual_failure")}
          description={t("commons.overview.header.legend.manual_failure.description")}
        >
          <SquareMarker
            height={9}
            width={9}
            color={color.raw.titanium600}
            fill={color.raw.titanium600}
          />
        </Marker>
      )}
    </>
  );
}

export default React.memo(DiagnosisLegend);
